import Skeleton from '../Skeleton';
import { SkelCard, Bar } from './homeSkeletonParts';

/**
 * Skeletons for the two "band" cards of the KAN-946 home dashboards: the month band
 * (admin: invoices + Jobs + Customers · billing: the invoices cell only — a stat strip) and the
 * Next-7-days card (KAN-1004: day tiles). Same borrowed-layout idea as homeSkeletonParts.js: the real card
 * shell and `cvx-home-*` band classes, filled with Skeleton bars.
 */

/** The muted caption label above a figure (`.blab`; no icon tile). */
function LabelRow({ width = 110 }) {
  return (
    <span className="cvx-home-blab" aria-hidden="true">
      <Skeleton width={width} height={11} borderRadius={6} />
    </span>
  );
}

/** Figure bar (+ caption bar) (+ a trend bar) — the HomeFigure md line. */
function ValueLine({ value = 76, caption = 0, trend = 0 }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }} aria-hidden="true">
      <Skeleton width={value} height={24} borderRadius={8} />
      {caption ? <Skeleton width={caption} height={11} borderRadius={6} /> : null}
      {trend ? <Skeleton width={trend} height={11} borderRadius={6} /> : null}
    </span>
  );
}

/** The 6px paid / not-paid bar + the dot legend line (the primary cell's body). */
function PaidBarLegend() {
  return (
    <>
      <Skeleton width="100%" height={6} borderRadius={999} />
      <Bar width="78%" height={11} line={18} />
    </>
  );
}

function MonthBandWeb({ variant }) {
  const admin = variant !== 'billing';
  return (
    <SkelCard layout="web" span={12} head={false} className="cvx-home-band">
      <div className="cvx-home-bcell" style={{ flex: '6 1 0' }} aria-hidden="true">
        <LabelRow width={130} />
        <ValueLine value={72} caption={36} />
        <PaidBarLegend />
      </div>
      {admin ? (
        <>
          <div className="cvx-home-bcell" style={{ flex: '3 1 0' }} aria-hidden="true">
            <LabelRow width={56} />
            <ValueLine value={56} trend={72} />
          </div>
          <div className="cvx-home-bcell" style={{ flex: '3 1 0' }} aria-hidden="true">
            <LabelRow width={84} />
            <ValueLine value={56} caption={52} />
          </div>
        </>
      ) : null}
    </SkelCard>
  );
}

function MonthBandCompact({ variant, layout }) {
  const admin = variant !== 'billing';
  return (
    <SkelCard layout={layout} titleWidth={admin ? 84 : 132} action={56}>
      <div className="cvx-home-block" aria-hidden="true">
        {admin ? <LabelRow width={64} /> : null}
        <ValueLine value={72} caption={36} />
        <PaidBarLegend />
      </div>
      {admin ? (
        <>
          <div className="cvx-home-hr" />
          <div className="cvx-home-cells" data-n={2} aria-hidden="true">
            <div className="cvx-home-cell">
              <LabelRow width={52} />
              <ValueLine value={48} trend={layout === 'phone' ? 0 : 64} />
            </div>
            <div className="cvx-home-cell">
              <LabelRow width={74} />
              <ValueLine value={48} caption={44} />
            </div>
          </div>
        </>
      ) : null}
    </SkelCard>
  );
}

/** The month band — `variant` 'admin' | 'billing'. */
export function SkelMonthBand({ variant, layout }) {
  return layout === 'web' ? <MonthBandWeb variant={variant} /> : <MonthBandCompact variant={variant} layout={layout} />;
}

/** One day tile: the weekday + date line, the count and the 4px load bar (the real `.cvx-home-wk__day` boxes). The phone's open strip stacks letter, count, load line, date. */
function WeekTile({ layout, span }) {
  const phone = layout === 'phone';
  const countHeight = 28;
  return (
    <div className="cvx-home-wk__day" style={{ cursor: 'default' }} aria-hidden="true">
      {phone ? <Bar width={8} height={10} line={14} /> : (
        <span className="cvx-home-wk__top">
          <Bar width={28} height={10} line={16} />
          <Bar width={14} height={11} line={16} />
        </span>
      )}
      <span className="cvx-home-wk__ct" style={{ height: countHeight }}>
        <Skeleton width={phone ? 18 : 28} height={countHeight - 4} borderRadius={8} />
      </span>
      <span className="cvx-home-wk__bar">
        <Skeleton width="100%" height={phone ? 3 : 4} borderRadius={99} />
      </span>
      {phone ? <Bar width={14} height={10} line={14} /> : null}
    </div>
  );
}

/** Next 7 days (the day-tile card): the real header (tile, title, count, link) + seven day tiles. One card on every layout (`span` = its 12-column share on web, 1-or-2 columns on tablet). */
export function SkelWeekBand({ layout, span, stretch = false }) {
  return (
    <SkelCard
      layout={layout}
      span={layout === 'phone' ? undefined : (span ?? 12)}
      stretch={layout !== 'phone' && stretch}
      className="cvx-home-weekcard"
      testId="week-skeleton"
      titleWidth={layout === 'phone' ? 84 : 112}
      pill={layout === 'phone' ? 96 : 124}
      action={layout === 'phone' ? 48 : 56}
    >
      <div className="cvx-home-center cvx-home-wkbox">
        <div className="cvx-home-wk">
          {Array.from({ length: 7 }, (_, i) => <WeekTile key={i} layout={layout} span={span} />)}
        </div>
      </div>
    </SkelCard>
  );
}
